/**
 * AutobuildPanel — the pipeline's side of a bug report / feature request.
 *
 * Rendered under a report on /home/bugs and inside the Plate task the
 * pipeline tags a director with. Shows, in order of what the viewer has to
 * do:
 *
 *   - the one open question (reporter or a director answers it here — the
 *     reply goes to `autobuild.answer` and reopens the report, which is what
 *     the scheduler resumes on);
 *   - the approval card while the build is parked on awaitingApproval: the
 *     three-line summary, before/after screenshots, the PR link, and — for
 *     directors (tenant.ownerIds) only — Approve / Request changes;
 *   - Redirect, for directors, on any build from understanding to awaiting
 *     approval: "you're building the wrong thing" — stops the run, keeps the
 *     branch and PR, and starts a fresh spec from the note;
 *   - the plain-English summary once it is done;
 *   - the thread: the people on the report and the pipeline's phases, in
 *     one conversation. Anyone can post; a director's post is an
 *     instruction for the build; every phase reads it before it starts and
 *     replies in it. Mirrored to #theo-builds.
 *
 * Everything it reads comes from shared/src/domain/autobuild.ts so the
 * connector and the scheduler agree on what each state means.
 */

import { useMemo, useState } from 'react';
import { Bot, Check, CornerUpLeft, ExternalLink, HelpCircle, Send, XCircle } from 'lucide-react';
import type { BugReport } from '@/services/bug-report.service';
import { answerAutobuildQuestion, decideAutobuild, postAutobuildThread, redirectAutobuild } from '@/services/bug-report.service';
import { getLocaleSync } from '@/utils/locale';
import {
  canRedirect,
  describeAutobuildPhase,
  hasOpenQuestion,
  isAwaitingDecision,
  liveEvidence,
  readAutobuild,
  threadView,
  validateDecision,
  validateRedirect,
  type AutobuildSource,
  type AutobuildVerdict,
  type ThreadItem,
  isParkedForDecision,
} from '@shared/domain/autobuild';

export interface AutobuildViewer {
  id: string;
  name: string;
  /** In tenant.ownerIds — the only people who can approve or redirect a build. */
  isDirector: boolean;
  /** tenant.ownerIds — whose thread posts count as instructions (the Director badge). */
  ownerIds?: string[];
}

interface AutobuildPanelProps {
  report: BugReport;
  viewer: AutobuildViewer;
  /** Where this panel lives — recorded on the decision. */
  via: Extract<AutobuildSource, 'theo' | 'plate'>;
  /** Open the decision box in this mode straight away (the Slack buttons' `?decide=`). */
  initialDecision?: AutobuildVerdict | null;
}

const TONE: Record<ReturnType<typeof describeAutobuildPhase>['tone'], string> = {
  wait: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  busy: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  ok: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  bad: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  muted: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

function when(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(getLocaleSync(), { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

export function AutobuildPanel({ report, viewer, via, initialDecision = null }: AutobuildPanelProps) {
  const ab = useMemo(() => readAutobuild(report.autobuild), [report.autobuild]);
  const phase = describeAutobuildPhase(ab.phase);
  const log = ab.log ?? [];

  const openQuestion = hasOpenQuestion(ab);
  const canAnswer = openQuestion && (viewer.id === report.reportedBy || viewer.isDirector);
  const awaiting = isAwaitingDecision(ab);
  const canDecide = awaiting && viewer.isDirector;
  const evidence = liveEvidence(ab);

  if (!ab.phase && log.length === 0 && (ab.thread?.length ?? 0) === 0) return null;

  return (
    <section
      className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 p-3 space-y-3"
      aria-label="Autobuild"
      data-testid="autobuild-panel"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Bot className="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" />
        <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">Autobuild</span>
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${TONE[phase.tone]}`}>{phase.label}</span>
        {ab.prUrl && (
          <a
            href={ab.prUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-blue-600 dark:text-blue-400 hover:underline"
          >
            <ExternalLink className="w-3 h-3" /> Pull request
          </a>
        )}
      </div>

      {ab.scope && (
        <p className="text-xs text-gray-700 dark:text-gray-300 break-words" data-testid="autobuild-scope">
          <span className="font-medium text-gray-500 dark:text-gray-400">Scope</span> — {ab.scope}
        </p>
      )}

      {openQuestion && (
        <QuestionBox
          report={report}
          question={ab.question ?? ''}
          canAnswer={canAnswer}
          viewer={viewer}
        />
      )}

      {isParkedForDecision(ab.phase) && (
        <ApprovalCard
          report={report}
          reviewSummary={ab.reviewSummary ?? ab.lastNote ?? ''}
          evidence={evidence}
          decision={ab.decision ?? null}
          awaiting={awaiting}
          canDecide={canDecide}
          via={via}
          initialMode={canDecide ? initialDecision : null}
        />
      )}

      {viewer.isDirector && canRedirect(ab) && <RedirectCard report={report} via={via} />}

      {ab.summary && ab.summary !== report.resolutionMessage && (
        <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 p-3">
          <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300 mb-1">What changed</p>
          <p className="text-sm text-emerald-900 dark:text-emerald-100 whitespace-pre-wrap break-words">{ab.summary}</p>
        </div>
      )}

      <ThreadView report={report} viewer={viewer} via={via} />
    </section>
  );
}

/**
 * Redirect — a director's "you're building the wrong thing", for any build
 * from understanding to awaiting approval (and one parked as blocked). The
 * note becomes the brief: the run in flight is stopped, the branch and the
 * PR are kept, and a fresh understand phase writes a new spec from the
 * note — it overrides the original request and any earlier spec. Request
 * changes (on the approval card) stays for small fixes after approval.
 */
export function RedirectCard({ report, via }: { report: BugReport; via: Extract<AutobuildSource, 'theo' | 'plate'> }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const submit = async () => {
    const check = validateRedirect(note);
    if (!check.ok) {
      setError(check.reason);
      return;
    }
    if (!report.id || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { cancelled } = await redirectAutobuild(report.id, check.note, via);
      setDone(
        cancelled === 'cancelled'
          ? 'Redirected — the run in flight was stopped. A fresh spec starts from your note within about 5 minutes.'
          : 'Redirected. A fresh spec starts from your note within about 5 minutes.',
      );
      setOpen(false);
      setNote('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not redirect the build');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="autobuild-redirect">
      {!open ? (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => {
              setOpen(true);
              setDone(null);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-800 text-amber-800 dark:text-amber-200 text-xs font-medium hover:bg-amber-50 dark:hover:bg-amber-900/20"
          >
            <CornerUpLeft className="w-3.5 h-3.5" /> Redirect
          </button>
          <span className="text-[11px] text-gray-500 dark:text-gray-400">
            {done ?? 'Building the wrong thing? Stop it and start again from your note.'}
          </span>
        </div>
      ) : (
        <div className="rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-2">
          <p className="text-xs font-medium text-amber-800 dark:text-amber-200">
            Redirect — what should it build instead? Your note becomes the spec: it overrides the original request and anything built so far.
          </p>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            autoFocus
            placeholder="e.g. Drop the X at the bottom — the real problem is the iPhone layout: size the job screen to the viewport. Keep the Android back-button fix."
            aria-label="Redirect note"
            className="w-full px-3 py-2 rounded-lg border border-amber-200 dark:border-amber-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white resize-none"
          />
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium disabled:opacity-50"
            >
              <CornerUpLeft className="w-4 h-4" /> {busy ? 'Redirecting…' : 'Stop and redirect'}
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setNote('');
                setError(null);
              }}
              disabled={busy}
              className="px-3 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
          {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
        </div>
      )}
    </div>
  );
}

const PHASE_CHIP = 'ml-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300';

/**
 * The thread — the people on the report and the pipeline's phases in one
 * conversation (threadView: `autobuild.thread` merged with the status lines).
 * Anyone with access can post; a director's post is marked as an
 * instruction. While the build is waiting on a question, a post is the
 * answer. The last few items show by default.
 */
export function ThreadView({
  report,
  viewer,
  via,
  showCount = 6,
}: {
  report: BugReport;
  viewer: AutobuildViewer;
  via: Extract<AutobuildSource, 'theo' | 'plate'>;
  showCount?: number;
}) {
  const ab = useMemo(() => readAutobuild(report.autobuild), [report.autobuild]);
  const items = useMemo(() => threadView(ab, viewer.ownerIds ?? []), [ab, viewer.ownerIds]);
  const [showAll, setShowAll] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const openQuestion = hasOpenQuestion(ab);

  const visible = showAll ? items : items.slice(-showCount);

  const post = async () => {
    if (!text.trim() || busy || !report.id) return;
    setBusy(true);
    setError(null);
    try {
      await postAutobuildThread(report.id, text, via);
      setText('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not post in the thread');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="autobuild-thread">
      {items.length > 0 && (
        <ol className="space-y-1" aria-label="Autobuild thread">
          {items.length > visible.length && (
            <li>
              <button type="button" onClick={() => setShowAll(true)} className="text-[11px] text-gray-500 dark:text-gray-400 hover:underline">
                Show all {items.length} in the thread
              </button>
            </li>
          )}
          {visible.map((it) => (
            <ThreadLine key={it.id} item={it} />
          ))}
        </ol>
      )}
      <div className="space-y-1.5">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          placeholder={
            openQuestion
              ? 'Reply here — your post answers the question and the build carries on.'
              : viewer.isDirector
                ? 'Write in the thread — the next phase reads it before it starts; your posts are instructions.'
                : 'Write in the thread — the next phase reads it before it starts.'
          }
          aria-label="Write in the thread"
          className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white resize-none"
        />
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={post}
            disabled={!text.trim() || busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900 text-xs font-medium disabled:opacity-50"
          >
            <Send className="w-3.5 h-3.5" /> {busy ? 'Posting…' : 'Post'}
          </button>
          <span className="text-[11px] text-gray-500 dark:text-gray-400">Also in #theo-builds.</span>
        </div>
        {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      </div>
    </div>
  );
}

function ThreadLine({ item }: { item: ThreadItem }) {
  const status = item.kind === 'status';
  return (
    <li className="flex gap-2 text-xs" data-kind={item.kind}>
      <span className="shrink-0 w-24 text-gray-400 dark:text-gray-500 tabular-nums">{when(item.at)}</span>
      <span className={`min-w-0 break-words ${status ? 'text-gray-500 dark:text-gray-400' : 'text-gray-800 dark:text-gray-200'}`}>
        {item.pipeline ? (
          <span className="font-medium text-gray-600 dark:text-gray-300">
            <Bot className="inline w-3 h-3 mr-0.5 align-[-2px]" aria-hidden="true" />
            Autobuild
            {item.phase && !status ? <span className={PHASE_CHIP}>{item.phase}</span> : null}
            {': '}
          </span>
        ) : (
          <span className="font-medium">
            {item.byName}
            {item.instruction ? (
              <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                Director
              </span>
            ) : null}
            {item.kind === 'redirect' ? <span className={PHASE_CHIP}>redirect</span> : null}
            {item.kind === 'answer' ? <span className={PHASE_CHIP}>answer</span> : null}
            {item.kind === 'decision' ? <span className={PHASE_CHIP}>decision</span> : null}
            {item.unanswered ? <span className={PHASE_CHIP}>waiting for a reply</span> : null}
            {': '}
          </span>
        )}
        {item.kind === 'question' ? <span className={PHASE_CHIP}>question</span> : null}
        <span className={item.kind === 'status' ? '' : 'whitespace-pre-wrap'}>{item.text}</span>
        {item.runUrl ? (
          <>
            {' '}
            <a href={item.runUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">
              run
            </a>
          </>
        ) : null}
      </span>
    </li>
  );
}

export function QuestionBox({
  report,
  question,
  canAnswer,
  viewer,
}: {
  report: BugReport;
  question: string;
  canAnswer: boolean;
  viewer: AutobuildViewer;
}) {
  const [answer, setAnswer] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    if (!answer.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      await answerAutobuildQuestion(report, answer, { id: viewer.id, name: viewer.name });
      setAnswer('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the answer');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-2">
      <div className="flex items-start gap-2">
        <HelpCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" aria-hidden="true" />
        <div className="min-w-0">
          <p className="text-xs font-medium text-amber-800 dark:text-amber-200">Autobuild needs one answer before building this</p>
          <p className="text-sm text-amber-900 dark:text-amber-100 whitespace-pre-wrap break-words">{question}</p>
        </div>
      </div>
      {canAnswer ? (
        <div className="space-y-2">
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            rows={2}
            placeholder="Your answer…"
            aria-label="Answer for autobuild"
            className="w-full px-3 py-2 rounded-lg border border-amber-200 dark:border-amber-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white resize-none"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={send}
              disabled={!answer.trim() || sending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium disabled:opacity-50"
            >
              <Send className="w-3.5 h-3.5" /> {sending ? 'Sending…' : 'Send answer'}
            </button>
            <span className="text-[11px] text-amber-700 dark:text-amber-300">The build picks it up within about 5 minutes.</span>
          </div>
          {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
        </div>
      ) : (
        <p className="text-[11px] text-amber-700 dark:text-amber-300">Waiting on {report.reportedByName || 'the reporter'}.</p>
      )}
    </div>
  );
}

export function ApprovalCard({
  report,
  reviewSummary,
  evidence,
  decision,
  awaiting,
  canDecide,
  via,
  initialMode,
}: {
  report: BugReport;
  reviewSummary: string;
  evidence: ReturnType<typeof liveEvidence>;
  decision: ReturnType<typeof readAutobuild>['decision'];
  awaiting: boolean;
  canDecide: boolean;
  via: Extract<AutobuildSource, 'theo' | 'plate'>;
  initialMode: AutobuildVerdict | null;
}) {
  const [mode, setMode] = useState<AutobuildVerdict | null>(initialMode);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (verdict: AutobuildVerdict) => {
    const check = validateDecision(verdict, note);
    if (!check.ok) {
      setError(check.reason);
      return;
    }
    if (!report.id || busy) return;
    setBusy(true);
    setError(null);
    try {
      await decideAutobuild(report.id, check.verdict, check.note, via);
      setMode(null);
      setNote('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the decision');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-blue-200 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/20 p-3 space-y-3" data-testid="autobuild-approval">
      <div>
        <p className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">Built, tested and audited — waiting for approval</p>
        {reviewSummary && (
          <p className="text-sm text-blue-900 dark:text-blue-100 whitespace-pre-wrap break-words">{reviewSummary}</p>
        )}
      </div>

      {evidence.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {evidence
            .filter((e) => /\.(png|jpe?g|gif|webp)(\?|$)/i.test(e.url))
            .map((e) => (
              <a key={e.url} href={e.url} target="_blank" rel="noopener noreferrer" className="block">
                <img
                  src={e.url}
                  alt={e.label}
                  className="w-28 h-20 object-cover object-top rounded-lg border border-blue-200 dark:border-blue-700 hover:opacity-80 transition-opacity"
                />
                <span className="block text-[10px] text-blue-700 dark:text-blue-300 mt-0.5">{e.label}</span>
              </a>
            ))}
          {/* The live walk-through's videos — a link each; the dashboard plays them. */}
          {evidence
            .filter((e) => e.kind === 'video' || /\.(webm|mp4)(\?|$)/i.test(e.url))
            .map((e) => (
              <a key={e.url} href={e.url} target="_blank" rel="noopener noreferrer" className="block text-[11px] text-blue-700 dark:text-blue-300 underline self-end">
                ▶ {e.label}
              </a>
            ))}
        </div>
      )}

      {!awaiting && decision && (
        <p className="text-xs text-blue-800 dark:text-blue-200">
          {decision.verdict === 'approve' ? 'Approved' : 'Changes requested'} by {decision.byName} · {when(decision.at)}
          {decision.note ? ` — ${decision.note}` : ''}. Picked up within about 5 minutes.
        </p>
      )}

      {canDecide && (
        <div className="space-y-2">
          {mode === null ? (
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setMode('approve')}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium"
              >
                <Check className="w-4 h-4" /> Approve
              </button>
              <button
                type="button"
                onClick={() => setMode('changes')}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <XCircle className="w-4 h-4" /> Request changes
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                autoFocus
                placeholder={mode === 'approve' ? 'Optional note' : 'What should change? The fix loop builds on your words.'}
                aria-label={mode === 'approve' ? 'Approval note' : 'What to change'}
                className="w-full px-3 py-2 rounded-lg border border-blue-200 dark:border-blue-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white resize-none"
              />
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => submit(mode)}
                  disabled={busy}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50 ${
                    mode === 'approve' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-amber-500 hover:bg-amber-600'
                  }`}
                >
                  {busy ? 'Saving…' : mode === 'approve' ? 'Approve and ship' : 'Send changes'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMode(null);
                    setNote('');
                    setError(null);
                  }}
                  disabled={busy}
                  className="px-3 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
        </div>
      )}

      {awaiting && !canDecide && (
        <p className="text-[11px] text-blue-700 dark:text-blue-300">Only a director can approve a build.</p>
      )}
    </div>
  );
}
